import { clsx, type ClassValue } from 'clsx';
import type { Severity } from '../data/types';

// Merge class names with clsx
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

// Format date for display
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// Format date with time. Carries the zone abbreviation (AB#29): analysts
// correlate against Defender (UTC) and customers in other states, so a bare
// wall-clock time is ambiguous. The zone is the browser's local zone.
export function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

/**
 * Short label of the browser's time zone for the given instant, e.g. "AEST",
 * "AEDT" or "GMT+10" (AB#29). Used where a component formats its own
 * timestamp and only needs the suffix.
 */
export function localTimeZoneLabel(date: Date = new Date()): string {
  try {
    const part = new Intl.DateTimeFormat('en-AU', { timeZoneName: 'short' })
      .formatToParts(date)
      .find((p) => p.type === 'timeZoneName');
    return part?.value ?? 'local';
  } catch {
    return 'local';
  }
}

// Format relative time (e.g., "2 hours ago")
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
}

// Get severity colour class
export function getSeverityClass(severity: Severity): string {
  const classes: Record<Severity, string> = {
    critical: 'severity-critical',
    high: 'severity-high',
    medium: 'severity-medium',
    low: 'severity-low',
    informational: 'severity-info',
  };
  return classes[severity] || 'severity-info';
}

// Get severity background colour for charts
export function getSeverityColor(severity: Severity): string {
  const colors: Record<Severity, string> = {
    critical: '#f85149',
    high: '#db6d28',
    medium: '#d29922',
    low: '#58a6ff',
    informational: '#3fb950',
  };
  return colors[severity] || '#8b949e';
}

// Truncate text with ellipsis
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

// Generate a short ID for display
export function shortId(id: string): string {
  if (id.length <= 8) return id;
  return id.slice(0, 8);
}

// Calculate risk score colour
export function getRiskScoreColor(score: number): string {
  if (score >= 8) return '#f85149'; // Critical
  if (score >= 6) return '#db6d28'; // High
  if (score >= 4) return '#d29922'; // Medium
  return '#3fb950'; // Low
}

// Sort helper for incidents
export function sortByDate(a: string, b: string, ascending = false): number {
  const dateA = new Date(a).getTime();
  const dateB = new Date(b).getTime();
  return ascending ? dateA - dateB : dateB - dateA;
}

// Filter incidents by severity
export function filterBySeverity<T extends { severity: Severity }>(
  items: T[],
  severities: Severity[]
): T[] {
  if (severities.length === 0) return items;
  return items.filter((item) => severities.includes(item.severity));
}

